import type { JSX } from 'react'
import { useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { HookAction, HookSource, ServerHook } from '@apiary/shared'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { NumberField, TextField } from '@/ui/primitives/Field'
import { Select } from '@/ui/primitives/Select'
import { Surface } from '@/ui/primitives/Surface'
import { Switch } from '@/ui/primitives/Switch'

/**
 * Редактор хуков сервера: «увидел в чате такое-то — сделай то-то».
 *
 * Причина существования этого редактора — тест-поле. Регулярное выражение,
 * написанное вслепую, проверяется в бою через сутки, когда сервер напишет
 * «5 минут до рестарта» и бот ничего не сделает. Поэтому здесь можно вставить
 * настоящую строку из чата и сразу увидеть, попадает она в шаблон или нет.
 */

export interface HookTestResult {
  ok: boolean
  matched: boolean
  error: string | null
}

/**
 * Проверка шаблона на строке.
 *
 * `g` и `y` из флагов убираются: с ними `RegExp.test` двигает `lastIndex`, и на
 * одном и том же вводе результат «моргает» между да и нет. Компиляция обёрнута в
 * try/catch, потому что незакрытая скобка в шаблоне — норма процесса, а не сбой
 * приложения.
 */
export function testHookPattern(pattern: string, flags: string, sample: string): HookTestResult {
  try {
    const regex = new RegExp(pattern, flags.replace(/[gy]/g, ''))
    return { ok: true, matched: regex.test(sample), error: null }
  } catch (error) {
    return {
      ok: false,
      matched: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

/** Новый хук с безопасными дефолтами: ничего не делает, пока его не настроят. */
export function emptyHook(): ServerHook {
  return {
    id: crypto.randomUUID(),
    name: '',
    match: { source: 'chat', pattern: '', flags: 'i' },
    action: { type: 'noop' },
    cooldownSec: 30,
    enabled: true
  }
}

/** Что мешает сохранить хук. `null` — всё в порядке. */
export function hookProblem(hook: ServerHook): string | null {
  if (hook.name.trim() === '') return 'нет названия'
  if (hook.match.pattern.trim() === '') return 'нет шаблона'
  if (!testHookPattern(hook.match.pattern, hook.match.flags, '').ok)
    return 'шаблон не компилируется'
  return null
}

const SOURCE_OPTIONS: { value: HookSource; label: string; note: string }[] = [
  { value: 'chat', label: 'Сообщение в чате', note: 'chat' },
  { value: 'kick', label: 'Причина кика', note: 'kick' },
  { value: 'title', label: 'Титры на экране', note: 'title' },
  { value: 'actionBar', label: 'Строка над хотбаром', note: 'actionBar' }
]

const ACTION_OPTIONS: { value: HookAction['type']; label: string }[] = [
  { value: 'chat', label: 'Написать в чат' },
  { value: 'reconnectAfter', label: 'Переподключиться через N минут' },
  { value: 'disconnect', label: 'Выйти с сервера' },
  { value: 'alert', label: 'Поднять тревогу в панели' },
  { value: 'noop', label: 'Ничего не делать' }
]

export function HookList({
  hooks,
  onChange
}: {
  hooks: ServerHook[]
  onChange: (hooks: ServerHook[]) => void
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {hooks.length === 0 ? (
        <p className="max-w-prose text-2xs text-ink-muted">
          Хуков нет. Самый частый — «за N минут до рестарта выйти и вернуться через N+2»: без него
          бот получает кик и полчаса ждёт следующей попытки.
        </p>
      ) : null}

      {hooks.map((hook, index) => (
        <HookCard
          key={hook.id}
          hook={hook}
          index={index}
          onChange={next => onChange(hooks.map((item, at) => (at === index ? next : item)))}
          onRemove={() => onChange(hooks.filter((_unused, at) => at !== index))}
        />
      ))}

      <Button
        variant="secondary"
        className="self-start"
        onClick={() => onChange([...hooks, emptyHook()])}
      >
        <Plus aria-hidden className="size-4" />
        Добавить хук
      </Button>
    </div>
  )
}

function HookCard({
  hook,
  index,
  onChange,
  onRemove
}: {
  hook: ServerHook
  index: number
  onChange: (hook: ServerHook) => void
  onRemove: () => void
}): JSX.Element {
  const [sample, setSample] = useState('')

  const result = useMemo(
    () => testHookPattern(hook.match.pattern, hook.match.flags, sample),
    [hook.match.pattern, hook.match.flags, sample]
  )

  const setAction = (type: HookAction['type']): void => {
    // Действие — размеченное объединение: при смене типа поля прежнего действия
    // не сохраняем, иначе в запрос уедет `minutes` у `chat`.
    const next: HookAction =
      type === 'chat'
        ? { type: 'chat', message: '' }
        : type === 'reconnectAfter'
          ? { type: 'reconnectAfter', minutes: 15 }
          : type === 'disconnect'
            ? { type: 'disconnect' }
            : type === 'alert'
              ? { type: 'alert', text: '' }
              : { type: 'noop' }
    onChange({ ...hook, action: next })
  }

  return (
    <Surface variant="flat" className="flex flex-col gap-4 bg-panel-2 p-3.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="font-display text-base font-semibold text-ink">
          {hook.name.trim() === '' ? `Хук ${index + 1}` : hook.name}
        </span>
        <div className="flex items-center gap-2">
          <Switch
            label="Включён"
            checked={hook.enabled}
            onCheckedChange={enabled => onChange({ ...hook, enabled })}
            align="left"
          />
          <IconButton label={`Удалить хук ${index + 1}`} size="sm" tone="danger" onClick={onRemove}>
            <Trash2 aria-hidden className="size-4" />
          </IconButton>
        </div>
      </div>

      <TextField
        label="Название"
        value={hook.name}
        required
        error={hook.name.trim() === '' ? 'Название нужно, чтобы понять хук в логе' : null}
        onChange={event => onChange({ ...hook, name: event.target.value })}
      />

      <div className="grid gap-4 sm:grid-cols-[1fr_7rem]">
        <Select
          label="Откуда берём текст"
          value={hook.match.source}
          onValueChange={value =>
            onChange({ ...hook, match: { ...hook.match, source: value as HookSource } })
          }
          options={SOURCE_OPTIONS}
        />
        <TextField
          label="Флаги"
          mono
          hint="i — без регистра"
          value={hook.match.flags}
          onChange={event =>
            onChange({ ...hook, match: { ...hook.match, flags: event.target.value } })
          }
        />
      </div>

      <TextField
        label="Шаблон (регулярное выражение)"
        mono
        value={hook.match.pattern}
        required
        placeholder="(\d+) минут[аы]? до рестарта"
        error={
          hook.match.pattern.trim() === ''
            ? 'Без шаблона хук не сработает никогда'
            : result.ok
              ? null
              : `Выражение не компилируется: ${result.error ?? ''}`
        }
        onChange={event =>
          onChange({ ...hook, match: { ...hook.match, pattern: event.target.value } })
        }
      />

      <div className="flex flex-col gap-2 rounded-sm border border-line bg-panel p-3">
        <TextField
          label="Проверить на строке"
          mono
          placeholder="Сервер: 5 минут до рестарта"
          value={sample}
          onChange={event => setSample(event.target.value)}
        />
        <p
          role="status"
          data-testid="hook-test-result"
          className="flex items-center gap-2 text-2xs"
        >
          {!result.ok ? (
            <Chip tone="fail" dot>
              Выражение с ошибкой
            </Chip>
          ) : sample === '' ? (
            <span className="text-ink-muted">Вставьте настоящую строку из чата сервера</span>
          ) : result.matched ? (
            <Chip tone="live" dot>
              Совпало — хук сработает
            </Chip>
          ) : (
            <Chip tone="warn" dot>
              Не совпало — хук промолчит
            </Chip>
          )}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Что делаем"
          value={hook.action.type}
          onValueChange={value => setAction(value as HookAction['type'])}
          options={ACTION_OPTIONS}
        />
        <NumberField
          label="Пауза между срабатываниями, с"
          hint="Защита от шторма: чат может повторить строку десять раз"
          value={hook.cooldownSec}
          min={0}
          max={3600}
          onChange={event => onChange({ ...hook, cooldownSec: Number(event.target.value) })}
        />
      </div>

      <ActionFields hook={hook} onChange={onChange} />
    </Surface>
  )
}

function ActionFields({
  hook,
  onChange
}: {
  hook: ServerHook
  onChange: (hook: ServerHook) => void
}): JSX.Element | null {
  const action = hook.action

  switch (action.type) {
    case 'chat':
      return (
        <TextField
          label="Сообщение в чат"
          mono
          placeholder="/afk"
          value={action.message}
          onChange={event =>
            onChange({ ...hook, action: { ...action, message: event.target.value } })
          }
        />
      )
    case 'reconnectAfter':
      return (
        <NumberField
          label="Вернуться через, минут"
          value={action.minutes}
          min={1}
          max={720}
          onChange={event =>
            onChange({ ...hook, action: { ...action, minutes: Number(event.target.value) } })
          }
        />
      )
    case 'disconnect':
      return (
        <TextField
          label="Причина выхода (попадёт в лог)"
          value={action.reason ?? ''}
          onChange={event =>
            onChange({ ...hook, action: { ...action, reason: event.target.value } })
          }
        />
      )
    case 'alert':
      return (
        <TextField
          label="Текст тревоги"
          value={action.text}
          onChange={event => onChange({ ...hook, action: { ...action, text: event.target.value } })}
        />
      )
    case 'noop':
      return null
  }
}
